// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Brim — the canvas stage: one luminous orb that swells with the charge, a "calm" ring
// that closes in and reddens as the hidden brim nears (the tell — shape AND colour, so
// it reads colour-blind), sparks, jolt shake + flash, and the release/overflow/spill
// bursts. Draws purely from GameState; owns no rules. Guards against 0-size measurements.

import { PALETTE } from './palette';
import { potAt, type GameEvent, type GameState } from './game';

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex)!;
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}
function mixHex(a: string, b: string, t: number): string {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  const r = Math.round(lerp(ca[0], cb[0], t));
  const g = Math.round(lerp(ca[1], cb[1], t));
  const bl = Math.round(lerp(ca[2], cb[2], t));
  return `rgb(${r},${g},${bl})`;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  size: number;
  color: string;
}

export class Board {
  private ctx: CanvasRenderingContext2D;
  private w = 0;
  private h = 0;
  private dpr = 1;
  private particles: Particle[] = [];
  private shake = 0;
  private flash = 0;
  private flashColor = '#ffffff';
  private t = 0;

  constructor(private canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext('2d')!;
    this.resize();
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const w = rect.width || this.canvas.clientWidth;
    const h = rect.height || this.canvas.clientHeight;
    if (w < 2 || h < 2) return; // ignore transient 0-size (backgrounded tab / mid-layout)
    this.dpr = Math.min(3, Math.max(1, window.devicePixelRatio || 1));
    this.w = w;
    this.h = h;
    this.canvas.width = Math.round(w * this.dpr);
    this.canvas.height = Math.round(h * this.dpr);
  }

  private center(): { cx: number; cy: number; half: number } {
    const cx = this.w / 2;
    const cy = this.h / 2;
    const half = (Math.min(this.w, this.h) / 2) * 0.92;
    return { cx, cy, half };
  }

  onEvents(events: GameEvent[], reducedMotion: boolean): void {
    const { cx, cy } = this.center();
    for (const e of events) {
      if (e.t === 'jolt') {
        if (!reducedMotion) this.shake = Math.max(this.shake, 5 + e.k * 12);
        this.flash = Math.max(this.flash, e.kind === 'flash' ? 0.5 : 0.28);
        this.flashColor = e.kind === 'feint' ? '#ffffff' : PALETTE.sheen;
      } else if (e.t === 'bank') {
        this.burst(cx, cy, 26, PALETTE.bank, 6.5);
        this.flash = Math.max(this.flash, 0.22);
        this.flashColor = PALETTE.bank;
      } else if (e.t === 'bust') {
        const col = e.kind === 'overflow' ? PALETTE.danger : PALETTE.sheen;
        this.burst(cx, cy, 40, col, 8);
        if (!reducedMotion) this.shake = Math.max(this.shake, 18);
        this.flash = Math.max(this.flash, 0.5);
        this.flashColor = PALETTE.danger;
      }
    }
  }

  private burst(x: number, y: number, n: number, color: string, speed: number): void {
    for (let i = 0; i < n; i++) {
      const a = (Math.PI * 2 * i) / n + Math.random() * 0.4;
      const sp = speed * (0.5 + Math.random());
      this.particles.push({
        x,
        y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        life: 1,
        max: 0.5 + Math.random() * 0.5,
        size: 2 + Math.random() * 3,
        color,
      });
    }
  }

  frame(s: GameState, opts: { dtMs: number; reducedMotion: boolean }): void {
    const { dtMs, reducedMotion } = opts;
    this.t += dtMs;
    const ctx = this.ctx;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, this.w, this.h);

    // Stage + edge vignette (the darker ring is a real surface the contrast test names).
    ctx.fillStyle = PALETTE.stage;
    ctx.fillRect(0, 0, this.w, this.h);
    const { cx, cy, half } = this.center();
    const vg = ctx.createRadialGradient(cx, cy, half * 0.4, cx, cy, half * 1.25);
    vg.addColorStop(0, 'rgba(0,0,0,0)');
    vg.addColorStop(1, PALETTE.vignette);
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, this.w, this.h);

    // Screen shake.
    let ox = 0;
    let oy = 0;
    if (this.shake > 0.2 && !reducedMotion) {
      ox = (Math.random() - 0.5) * this.shake;
      oy = (Math.random() - 0.5) * this.shake;
      this.shake *= Math.exp(-dtMs / 90);
    } else {
      this.shake = 0;
    }
    ctx.save();
    ctx.translate(ox, oy);

    const agit = clamp(s.agitation, 0, 1.15);
    const danger = clamp((agit - 0.45) / 0.55, 0, 1); // 0 until ~45% then ramps to the brim

    // The "calm" ring: closes in and reddens with danger; jagged shiver near the brim.
    if (s.holding || !s.over) {
      const ringR = lerp(half * 0.98, half * 0.62, danger);
      const ringCol = mixHex(PALETTE.ring, PALETTE.danger, danger);
      ctx.lineWidth = lerp(2, 5, danger);
      ctx.strokeStyle = ringCol;
      ctx.globalAlpha = 0.5 + 0.4 * danger;
      ctx.beginPath();
      const seg = 72;
      for (let i = 0; i <= seg; i++) {
        const a = (Math.PI * 2 * i) / seg;
        const jag = reducedMotion ? 0 : danger * 5 * Math.sin(a * 6 + this.t / 70);
        const rr = ringR + jag;
        const x = cx + Math.cos(a) * rr;
        const y = cy + Math.sin(a) * rr;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    // The orb — radius grows with the charge; edge reddens and gains spikes with danger.
    if (s.holding) {
      const fill = clamp(s.pot / 280, 0, 1);
      const baseR = lerp(half * 0.12, half * 0.44, fill);
      const shiver = reducedMotion ? 0 : danger * 3.5 * Math.sin(this.t / 45);
      const R = baseR + shiver;
      const edge = mixHex(PALETTE.orbEdge, PALETTE.danger, danger * 0.85);

      // outer glow
      const glow = ctx.createRadialGradient(cx, cy, R * 0.2, cx, cy, R * 1.7);
      glow.addColorStop(0, mixHex(PALETTE.orbMid, PALETTE.danger, danger * 0.5));
      glow.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.globalAlpha = 0.5;
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(cx, cy, R * 1.7, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;

      // spikes near the brim (a shape cue independent of colour)
      if (danger > 0.15 && !reducedMotion) {
        const spikes = 16;
        ctx.fillStyle = edge;
        for (let i = 0; i < spikes; i++) {
          const a = (Math.PI * 2 * i) / spikes + this.t / 400;
          const len = R * (1 + danger * (0.14 + 0.1 * Math.sin(this.t / 60 + i)));
          ctx.beginPath();
          ctx.moveTo(cx + Math.cos(a - 0.06) * R, cy + Math.sin(a - 0.06) * R);
          ctx.lineTo(cx + Math.cos(a) * len, cy + Math.sin(a) * len);
          ctx.lineTo(cx + Math.cos(a + 0.06) * R, cy + Math.sin(a + 0.06) * R);
          ctx.closePath();
          ctx.fill();
        }
      }

      // the body (amber gradient), with a bright sheen
      const body = ctx.createRadialGradient(cx - R * 0.34, cy - R * 0.4, R * 0.1, cx, cy, R);
      body.addColorStop(0, PALETTE.orbCore);
      body.addColorStop(0.5, mixHex(PALETTE.orbMid, PALETTE.danger, danger * 0.6));
      body.addColorStop(1, edge);
      ctx.fillStyle = body;
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = PALETTE.sheen;
      ctx.globalAlpha = 0.8;
      ctx.beginPath();
      ctx.arc(cx - R * 0.34, cy - R * 0.4, R * 0.22, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;

      // the charge value, dark ink on the bright core (contrast-pinned as text)
      ctx.fillStyle = PALETTE.ink;
      ctx.font = `700 ${Math.round(clamp(R * 0.5, 16, 64))}px ui-sans-serif, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const shown = Math.round(s.pot * s.multiplier);
      ctx.fillText(String(shown), cx, cy);
    }

    // Particles.
    this.particles = this.particles.filter((p) => p.life > 0);
    for (const p of this.particles) {
      p.x += p.vx;
      p.y += p.vy;
      p.vx *= 0.96;
      p.vy *= 0.96;
      p.life -= dtMs / 1000 / p.max;
      ctx.globalAlpha = clamp(p.life, 0, 1);
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.restore();

    // Full-screen flash (jolt / bank / bust) — drawn over the shake.
    if (this.flash > 0.01) {
      ctx.globalAlpha = this.flash;
      ctx.fillStyle = this.flashColor;
      ctx.fillRect(0, 0, this.w, this.h);
      ctx.globalAlpha = 1;
      this.flash *= Math.exp(-dtMs / 120);
    } else {
      this.flash = 0;
    }
  }

  destroy(): void {
    this.particles = [];
  }
}

/** The pot a fresh orb would reach at time t — re-exported so the HUD can label ceilings. */
export { potAt };
